/* eslint-disable react/prop-types */
import React, {
  useState, useEffect, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactDOM from 'react-dom';
import { Droppable } from 'react-beautiful-dnd';
import {
  List,
  AutoSizer,
  CellMeasurer,
  /** CellMeasurerCache, */
} from 'react-virtualized';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
/** we will need this global cache for setting placeholder logic in week view */
import { getCacheForDroppable } from '../../utils/virtualizationCache';
import {
  setSelectedItems,
  setExpandedItems,
  setCalendarContainerScrollPosition,
  setIsBeforeRecurringSectionExpanded,
  setIsAfterRecurringSectionExpanded,
  clearMostRecentlyAddedCalendarItemTracker,
} from '../../ducks/calendarActions';
import DraggableDivWrapper from './DraggableDivWrapper';
import SelectedItemsTextAndCheckBoxContainerWrapper from './SelectedItemsTextAndCheckBoxContainerWrapper';
import DropHereSection from './DropHereSection';
import ExercisePicker from './ExercisePicker';
import RenderCloneDraggableDivWrapper from './RenderCloneDraggableDivWrapper';
import AfterRecurringVirtualRow from './AfterRecurringVirtualRow';
import BeforeRecurringVirtualRow from './BeforeRecurringVirtualRow';
import PlaceholderForVirtualizedList from './PlaceholderForVirtualizedList';

const DailyAndRecurring = styled('div')`
  display: flex;
  flex-direction: column;
  height: auto;
`;

const DroppableDiv = styled('div')`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  background: ${(props) => (props.isDragging && props.dragSource !== props.id ? '#FCFCFC' : 'white')};
  margin-bottom: ${(props) => (props.isDragging && props.dragSource !== props.id && !props.noMarginBottom && '10px')};
`;

const DraggableContainer = ({
  id,
  date,
  dayKey,
  isWeekView,
  isDayExpanded,
  onDayExpand,
  expandDay,
}) => {
  const dispatch = useDispatch();
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const draggedItem = useSelector((state) => state.calendar.data.draggedItem);
  const onBeforeCapture = useSelector((state) => state.calendar.data.onBeforeCapture);
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector((state) => state.calendar.data.afterRecurringWorkouts);
  const dragSource = useSelector((state) => state.calendar.data.dragSource);
  const isDragEventHappening = useSelector((state) => state.calendar.data.isDragging);
  const mostRecentlyAddedCalendarItem = useSelector(
    (state) => state.calendar.data.mostRecentlyAddedCalendarItem,
  );
  const weekViewHorizontallyExpandedDay = useSelector(
    (state) => state.calendar.data.weekViewExpandedDay,
  );
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const selectedItems = useSelector((state) => state.calendar.data.selectedItems);
  /** Build matching items based on current calendar items */
  const matchingItems = {};

  const effectiveDate = date || selectedDate;
  const effectiveSelectedProgramDayNumber = date || selectedProgramDayNumber;

  /** here we are conditionally setting calendar items to the true calendar items or
   * the non date based items. We are doing it here so we don't have to write conditionals
   * everywhere we use calendarItems
   */
  const calendarItems = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems[
      effectiveSelectedProgramDayNumber
    ]?.exercises || [])
    : useSelector((state) => state.calendar.data.calendarItems[
      moment(effectiveDate).format('YYYY-MM-DD')
    ]?.exercises || []);

  calendarItems.forEach((item) => {
    if (item && item.assignedId && selectedItems[item.assignedId]) {
      matchingItems[item.assignedId] = selectedItems[item.assignedId];
    }
  });

  const itemsAreSelected = Object?.keys(matchingItems)?.length;

  const [scrollToIndex, setScrollToIndex] = useState(undefined);

  /** navigation header that can have collapsing content so height needs to be dynamic */
  const getHeaderHeight = () => headerHeight;

  const getOtherConditionalHeaderAndSubHeaderHeights = () => {
    const teambuildrLayoutHeaderHeight = 49;
    const containerHeaderHeightDayView = programType === 'free-form-program' ? 58 : 80;
    const containerHeaderHeightWeekView = programType === 'free-form-program' ? 58 : 80;
    /** adding + 14 below for margins */
    const calendarSubHeaderDateAndWeekHeightDayView = programType === 'free-form-program' ? 0 : 32;
    const calendarSubHeaderDateAndWeekHeightWeekView = programType === 'free-form-program' ? 0 : 32;
    const weekViewToggleFooter = programType === 'free-form-program' ? 0 : 58;
    const wrapperMargin = 8 + 8;
    const weekViewFooter = programType === 'free-form-program' ? 50 + 16 : 0;
    const dayViewFooter = programType === 'free-form-program' ? 50 : 0;

    return teambuildrLayoutHeaderHeight
    + (viewType === 'weekView' ? containerHeaderHeightWeekView : containerHeaderHeightDayView)
    + (viewType === 'weekView' ? calendarSubHeaderDateAndWeekHeightWeekView : calendarSubHeaderDateAndWeekHeightDayView)
    + (viewType === 'weekView' ? weekViewToggleFooter : 0)
    + wrapperMargin
    + (viewType === 'weekView' ? weekViewFooter : dayViewFooter);
  };

  useEffect(() => {
    dispatch(setSelectedItems({}));
    dispatch(setExpandedItems({}));
    dispatch(setIsBeforeRecurringSectionExpanded(false));
    dispatch(setIsAfterRecurringSectionExpanded(false));
  }, [selectedReduxCalendar,
    selectedDate,
    selectedProgramDayNumber,
    selectedReduxNonDateBasedProgram]);

  useEffect(() => {
    dispatch(setIsBeforeRecurringSectionExpanded(false));
    dispatch(setIsAfterRecurringSectionExpanded(false));
  }, [weekViewHorizontallyExpandedDay]);

  const formatDate = (dateToFormat) => `${moment(dateToFormat).format('YYYY-MM-DD')}`;

  /**
   * scroll-start handler (called via react-virtualized's onScroll)
   * Scroll handler that dispatches scroll position ONLY on initial scroll start.
   * This is used to close any open dropdowns (exercise picker, calendar dropdown, etc.)
   * when the user begins scrolling.
   */
  const isScrollingRef = useRef(false);
  const handleScroll = ({ scrollTop }) => {
    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
      dispatch(setCalendarContainerScrollPosition(scrollTop));
    }

    // eslint-disable-next-line no-underscore-dangle
    clearTimeout(window._calendarScrollTimeout);
    // eslint-disable-next-line no-underscore-dangle
    window._calendarScrollTimeout = setTimeout(() => {
      isScrollingRef.current = false;
    }, 150);
  };

  const hasBefore = !!(beforeRecurringWorkouts[formatDate(effectiveDate)]
  && beforeRecurringWorkouts[formatDate(effectiveDate)].length) && programType !== 'free-form-program';

  const hasAfter = !!(afterRecurringWorkouts[formatDate(effectiveDate)]
  && afterRecurringWorkouts[formatDate(effectiveDate)].length) && programType !== 'free-form-program';

  const lastAutoScrolledIdRef = useRef(null);

  /** this code set the scroll index for the virtual list to scroll to the index
   * of the most recently added item, this is expecially importent
   * when we add an item from the create exercise forms
   */
  useEffect(() => {
    if (!mostRecentlyAddedCalendarItem) return;

    const mostRecentlyAddedId = mostRecentlyAddedCalendarItem.assignedId?.toString();
    if (lastAutoScrolledIdRef.current === mostRecentlyAddedId) return; // already did this one

    const realIndex = calendarItems.findIndex(
      (it) => it.assignedId === mostRecentlyAddedCalendarItem.assignedId,
    );
    if (realIndex === -1) return;

    const beforeOffset = hasBefore ? 1 : 0;
    const targetIndex = beforeOffset + realIndex;

    lastAutoScrolledIdRef.current = mostRecentlyAddedId;
    setScrollToIndex(targetIndex);
    /** we might need this here just need to test more when adding items
     * and making sure it doesn't get caught in a loop scrolling to the index on add
     */
    dispatch(clearMostRecentlyAddedCalendarItemTracker());
  }, [mostRecentlyAddedCalendarItem, calendarItems, hasBefore]);

  /** clear scrollToIndex so it doesn't stick around */
  useEffect(() => {
    if (scrollToIndex === undefined) return;
    const raf = window.requestAnimationFrame(() => {
      setScrollToIndex(undefined);
    });
    // eslint-disable-next-line consistent-return
    return () => window.cancelAnimationFrame(raf);
  }, [scrollToIndex]);

  const totalRowCount = (hasBefore ? 1 : 0)
    + calendarItems.length
    + 1 /** exercise picker */
    + (hasAfter ? 1 : 0)
    + (onBeforeCapture ? 1 : 0);

  /** we need this global cache and clear all for week view
   * we need access to the caches globally to set redux data in dragmanager on drag start
   * and on drag end so we get get the heights and use the height of the dragged item
   * to set the height of the placeholder that will be neede when dragging and dropping
   * accross droppable containers
   */
  /** passing the droppable id to create the global cache for this droppable */
  const cache = getCacheForDroppable(id);
  /** need to clear cache on every mount so we get accurate cell measure heights
   * each mount and not stale of old ones.
   */
  cache.clearAll();

  /** compute offsets */
  const beforeBlockOffset = hasBefore ? 1 : 0;
  const onBeforeCaptureOffset = onBeforeCapture ? 1 : 0;
  const onBeforeCaptureIndex = beforeBlockOffset
  + calendarItems.length;
  const placeholderIndex = onBeforeCaptureIndex + onBeforeCaptureOffset;

  const MemoizedDraggableItems = React.memo(({
    itemForMemo,
    indexForMemo,
    idForMemo,
    measureFunction,
    cacheForMemo,
    isWeekViewForMemo,
    isDayExpandedForMemo,
    effectiveDateForMemo,
    effectiveSelectedProgramDayNumberForMemo,
    onDayExpandForMemo,
  }) => (
    <DraggableDivWrapper
      item={itemForMemo}
      index={indexForMemo}
      id={idForMemo}
      measure={measureFunction}
      cache={cacheForMemo}
      isWeekView={isWeekViewForMemo}
      isDayExpanded={isDayExpandedForMemo}
      date={effectiveDateForMemo}
      dayNumber={effectiveSelectedProgramDayNumberForMemo}
      onDayExpand={onDayExpandForMemo}
    />
  ));

  const rowRenderer = ({
    index,
    key,
    style,
    parent,
    isUsingPlaceholder,
    /** hideExercisePicker, */
    showPlaceholder,
    isDraggingOver,
  }) => (
    <CellMeasurer
      cache={cache}
      columnIndex={0}
      key={key}
      parent={parent}
      rowIndex={index}
    >
      {({ measure, registerChild }) => {
        let content = null;

        /** compute offsets */
        const placeholderOffset = isUsingPlaceholder && !onBeforeCapture ? 1 : 0;
        const exercisePickerIndex = placeholderIndex + placeholderOffset;
        const afterBlockIndex = exercisePickerIndex + 1;

        /** Before recurring section */
        if (hasBefore && index === 0) {
          content = (
            <BeforeRecurringVirtualRow
              measure={measure}
              cache={cache}
              date={effectiveDate}
              dayKey={dayKey}
              onDayExpand={onDayExpand}
              isDayExpanded={isDayExpanded}
            />
          );
        }

        /** Real calendar items */
        if (index >= (hasBefore ? 1 : 0) && index < (hasBefore ? 1 : 0)
          + calendarItems.length) {
          const realIndex = index - (hasBefore ? 1 : 0);
          const item = calendarItems[realIndex];

          content = (
            <>
              {item ? (
                <MemoizedDraggableItems
                  itemForMemo={item}
                  indexForMemo={realIndex}
                  idForMemo={id}
                  measureFunction={measure}
                  cacheForMemo={cache}
                  isWeekViewForMemo={isWeekView}
                  isDayExpandedForMemo={isDayExpanded}
                  effectiveDateForMemo={effectiveDate}
                  effectiveSelectedProgramDayNumberForMemo={effectiveSelectedProgramDayNumber}
                  onDayExpandForMemo={onDayExpand}
                />
              ) : null}
            </>
          );
        }

        /** Drop Here prompt if less than 2 items */
        if (onBeforeCapture && index === onBeforeCaptureIndex) {
          /** figuring out the height that the drop here seciton needs to be to take up
           * the remaining height in the doppable area to let the user see the dropzone
           */
          const totalHeight = parent.props.height; /** AutoSizer List height */
          const usedHeightAbove = hasBefore ? 68 : 0;
          const exercisePickerHeight = hasAfter ? 45 : 50;
          const afterHeight = hasAfter ? 68 : 0;

          /** Compute remaining height */
          const remainingHeight = Math.max(
            68, /** min height */
            totalHeight - usedHeightAbove - exercisePickerHeight - afterHeight,
          );
          content = (
            <div style={{
              height: calendarItems.length ? 100 : remainingHeight,
              display: 'flex',
              border: !calendarItems.length && '1px dashed #77A7FF',
              borderRadius: 4,
            }}
            >
              <DropHereSection id={id} />
            </div>
          );
        }

        /** Placeholder row */
        if (isUsingPlaceholder && !onBeforeCapture && index === placeholderIndex) {
          content = (
            <PlaceholderForVirtualizedList
              measure={measure}
              showPlaceholder={showPlaceholder}
              isDraggingOver={isDraggingOver}
              droppableId={id}
            />
          );
        }

        if (index === exercisePickerIndex) {
          content = (
            <ExercisePicker
              isWeekView={isWeekView}
              calendarItemsHaveLength={calendarItems?.length > 0}
              hasBefore={hasBefore}
              hasAfter={hasAfter}
              date={effectiveDate}
              expandDay={expandDay}
            />
          );
        }

        /* After recurring section */
        if (hasAfter && index === afterBlockIndex) {
          content = (
            <AfterRecurringVirtualRow
              measure={measure}
              cache={cache}
              date={effectiveDate}
              dayKey={dayKey}
              onDayExpand={onDayExpand}
              isDayExpanded={isDayExpanded}
            />
          );
        }

        return (
          <div ref={registerChild} style={{ ...style }}>
            {content}
          </div>
        );
      }}
    </CellMeasurer>
  );

  return (
    <>
      {!!calendarItems.length && !isWeekView && (
        <SelectedItemsTextAndCheckBoxContainerWrapper />
      )}
      <DailyAndRecurring
        onBeforeCapture={onBeforeCapture}
        calendarItemLength={calendarItems?.length}
      >
        {/** Note: beforeRecurring and afterRecurring are now
         * rendered inside the list as fake rows */}
        <Droppable
          isCombineEnabled
          droppableId={id}
          isDropDisabled={viewType === 'weekView' && draggedItem?.type === 'session_break' && draggedItem?.sourceDroppableId !== id}
          mode='virtual'
          renderClone={(provided, snapshot, rubric) => {
            const item = calendarItems[rubric.source.index];
            return (
              <RenderCloneDraggableDivWrapper
                item={item}
                index={rubric.source.index}
                id={id}
                provided={provided}
                snapshot={snapshot}
                date={effectiveDate}
              />
            );
          }}
        >
          {(droppableProvided, snapshot) => {
            const showPlaceholder = snapshot?.isUsingPlaceholder && snapshot?.draggingOverWith
             && !snapshot?.draggingOverWith?.includes(id) && !snapshot?.draggingFromThisWith;
            const totalItems = totalRowCount + 1; /** permanent placeholder is the +1 */
            return (
              <DroppableDiv
                noMarginBottom={(afterRecurringWorkouts[formatDate(effectiveDate)]?.length
                  && afterRecurringWorkouts[formatDate(effectiveDate)]?.length) || viewType === 'weekView'}
                isDragging={isDragEventHappening}
                dragSource={dragSource}
                calendarItemLength={calendarItems?.length}
                id={id}
                onBeforeCapture={onBeforeCapture}
                style={{
                  /** wee need to calculate the height exactly so we
                   * need to get the dynamic header height, and subtract
                   * any fixed headers, the conditional stats bar and the added padding
                   * from the bulk actions container when items are selected.
                 */
                  height: `calc(100vh - ${getHeaderHeight()}px - ${getOtherConditionalHeaderAndSubHeaderHeights()}px - ${isAnalyticsDisabled ? '0px' : '40px'} - ${calendarItems?.length && viewType !== 'weekView' ? '40px' : '0px'} - ${itemsAreSelected > 0 ? '20px' : '0px'})`,
                  // border: '1px solid red',
                  // overflow: 'hidden',
                }}
              >
                <AutoSizer>
                  {({ width, height }) => (
                    <List
                      {...droppableProvided.droppableProps}
                      style={{
                        backgroundColor: 'white',
                        scrollbarWidth: isWeekView ? 'none' : 'thin',
                        overflowY: 'scroll',
                      }}
                      height={height}
                      width={width}
                      onScroll={handleScroll}
                      /** scroll to index allows us to scroll to
                       * an index of a recently added item
                       * but we must clear the item right after we scroll to
                       * index so it doesn't stay in redux and continue to
                       * bounce to that index on remounts */
                      scrollToIndex={scrollToIndex}
                      rowCount={totalItems}
                      rowRenderer={(rowRenderArgs) => rowRenderer({
                        ...rowRenderArgs, isUsingPlaceholder: true, draggingFromThisWith: snapshot?.draggingFromThisWith, hideExercisePicker: snapshot?.isUsingPlaceholder && snapshot?.draggingOverWith?.includes('sideBar'), showPlaceholder, isDraggingOver: snapshot?.isDraggingOver,
                      })}
                      rowHeight={cache.rowHeight}
                      deferredMeasurementCache={cache}
                      overscanRowCount={1}
                      /** this estimated size helps to make sure we don't get
                       * stuck only being able to scroll half way
                       * we will over estimate the scrollable area by a lot to make sure
                      */
                      estimatedRowSize={500}
                      ref={(ref) => {
                        if (ref) {
                          /** bridge inner DOM node to react-beautiful-dnd */
                          // eslint-disable-next-line react/no-find-dom-node
                          const whatHasMyLifeComeTo = ReactDOM.findDOMNode(ref);
                          if (whatHasMyLifeComeTo instanceof HTMLElement) {
                            droppableProvided.innerRef(whatHasMyLifeComeTo);
                          }
                        }
                      }}
                    />
                  )}
                </AutoSizer>
              </DroppableDiv>
            );
          }}
        </Droppable>
      </DailyAndRecurring>
    </>
  );
};

DraggableContainer.propTypes = {
  id: PropTypes.string.isRequired,
  date: PropTypes.string,
  isWeekView: PropTypes.bool,
  isDayExpanded: PropTypes.bool,
  onDayExpand: PropTypes.func,
};

DraggableContainer.defaultProps = {
  date: null,
  isWeekView: false,
  isDayExpanded: false,
  onDayExpand: () => {},
};

export default DraggableContainer;
